import {
	useState,
	useEffect,
	Suspense,
	Dispatch,
	SetStateAction,
	memo,
} from "react";
import { Row, Col } from "rsuite";
import SummaryTable from "../../../components/SummaryTable";

import ScatterGraph from "../../../components/scatter-graph";
import {
	getCPCustomerList,
	getCPSummaryData,
} from "../../../queries/ScenarioForecast";
import useFilter from "../../../hooks/useFilter";
import { CustomerScatterWithZoom } from "./CustomerScatterGraph";

type Props = {
	setLoading: Dispatch<SetStateAction<number>>;
	handleCustomerChange: (customer: string) => void;
	handleScatterSelection: () => void;
	isFilterExpanded: boolean,
	filterValues: any
};

const Summary = ({
	filterValues,
	setLoading,
	handleCustomerChange,
	handleScatterSelection,
	isFilterExpanded
}: Props) => {
	const [scatterGraphData, setCatterGraphData] = useState<any>();
	const [selectedCustomerId, setSelectedCustomerId] = useState<string>("");
	const [summaryData, setSummaryData] = useState<any>(null);
	const [averageLine, setAverageLine] = useState<AverageLine>();

	const { selectedFilter } = useFilter();
	useEffect(() => {
		if ((selectedFilter == "electrical" && filterValues?.district && filterValues?.date) ||
			selectedFilter == "plumbing"
		)
			fetchCustomerList();
	}, [filterValues?.date, filterValues?.district, filterValues?.project, filterValues?.pcName]);

	useEffect(() => {
		handleSummaryTabScatterGrphCall({ id: filterValues?.customer || "" });
	}, [filterValues?.customer]);

	const getSummaryData = async (customer: string) => {
		setLoading((prev) => prev + 1);
		try {
			let body = { customerId: customer, dateRange: filterValues?.date, projectFlag: filterValues?.project || "All", District: filterValues?.district }

			const res = await getCPSummaryData(body);
			setSummaryData(res?.data?.data);
		} catch (err) {
			console.log("error in get APi --", err);
		} finally {
			setLoading((prev) => prev - 1);
		}
	};

	const handleSummaryTabScatterGrphCall = (val: any) => {
		if (filterValues.district && filterValues.date) {
			handleCustomerChange(val?.id)
			getSummaryData(val?.id);
			setSelectedCustomerId(val?.id);
		}
	};

	const fetchCustomerList = async () => {
		setLoading((prev) => prev + 1);
		try {
			let body: any = { dateRange: filterValues.date };
			if (selectedFilter == "electrical") {
				body = { ...body, projectFlag: filterValues.project || "All", District: filterValues.district, customerId: filterValues?.customer, }
			}
			else if (selectedFilter == "plumbing") {
				body = { ...body, pc: filterValues.pcName, }
			}
			const res = await getCPCustomerList(body, selectedFilter);
			if (selectedFilter == "electrical") {
				setCatterGraphData(res?.data?.data);
				setAverageLine(res?.data?.avgLineData?.[0]);
				handleSummaryTabScatterGrphCall({ id: filterValues.customer ? filterValues.customer : "" });
			}
			else if (selectedFilter == "plumbing") {
				setCatterGraphData(res?.data);
			}
		} catch (err) {
			console.log("error in get APi --", err);
		} finally {
			setLoading((prev) => prev - 1);
		}
	};

	const handleGraphCall = (val: any) => {
		handleCustomerChange(val?.id)
		setSelectedCustomerId(val?.id);
		if (val?.id) {
			handleScatterSelection();
		}
	}
	return (
		<div className="h-full ml-[1vh]">
			{selectedFilter == "electrical" &&
				<>
					<div className="chart-title subHead flex items-center !w-full">
						Gross Margin vs Net Margin per Drop by Customers
					</div>
					<Row className={`flex ${isFilterExpanded ? "h-[55vh]" : "h-[63vh]"} w-full`}>
						<Col
							xs={24}
							sm={24}
							md={5}
							className="flex flex-col justify-between "
						>
							<div>
								<SummaryTable data={summaryData?.OptimizeMargin} />
							</div>
							<div>
								<SummaryTable data={summaryData?.ReassesAndFix} />
							</div>
						</Col>
						<Col xs={24} sm={24} md={8} className="h-full flex-1 mx-[12vw]">
							<div
								className="border-[0.5px] border-lightGrey shadow-md h-full "
								id="graph"
							>
								<Suspense fallback={"loading here..."}>
									<ScatterGraph
										selectedCustomerId={selectedCustomerId}
										data={scatterGraphData || []}
										handleSummaryTabScatterGrphCall={
											handleGraphCall
										}
										refX={
											(averageLine?.avg_deliveredMargin || 0) *
											100
										}
										refY={(averageLine?.avg_grossMargin || 0) * 100}
									/>
								</Suspense>
							</div>
						</Col>

						<Col
							xs={24}
							sm={24}
							md={5}
							className="flex flex-col justify-between "
						>
							<div>
								<SummaryTable data={summaryData?.GrowCustomer} />
							</div>
							<div>
								<SummaryTable data={summaryData?.PriceForProfit} />
							</div>
						</Col>
					</Row>
				</>
			}
			{selectedFilter == "plumbing" &&
				<div className={`flex gap-6 ${isFilterExpanded ? "h-[55vh]" : "h-[63vh]"} w-full`}>
					<div className="w-1/2">
						<div className="subHead flex items-center !w-full">
							PC Level View
						</div>
						<CustomerScatterWithZoom
							isPCGraph={true}
							scatterGraphData={scatterGraphData?.graph2?.data || []}
							selectedCustomer={filterValues?.customer || ""}
							selectedPC={filterValues?.pcName || ""}
							thresholdX={scatterGraphData?.graph2?.thresholdX || 0}
							thresholdY={scatterGraphData?.graph2?.thresholdY || 0}
							filterExpansion={isFilterExpanded}
						/>
					</div>
					<div className="w-1/2">
						<div className="subHead flex items-center !w-full">
							Customer Level View(Top 500)
						</div>
						<CustomerScatterWithZoom
							isPCGraph={false}
							scatterGraphData={scatterGraphData?.graph1?.data || []}
							selectedCustomer={filterValues?.customer || ""}
							selectedPC={filterValues?.pcName || ""}
							thresholdX={scatterGraphData?.graph1?.thresholdX || 0}
							thresholdY={scatterGraphData?.graph1?.thresholdY || 0}
							filterExpansion={isFilterExpanded}
						/>
					</div>
				</div>
			}
		</div>
	);
};

export default memo(Summary);
